import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Star, Shield, Zap, MessageCircle, ShoppingCart, Heart, Rocket, Flame, Info } from "lucide-react";

import SellerCard from "../components/SellerCard";

import api, { API } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";
import { hasInstantDelivery } from "../lib/product";

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  // Data tujuan topup untuk produk instan undeadshopp (User ID + Zone/Server).
  const [gameFields, setGameFields] = useState({ user_id: "", zone_id: "" });
  const [rating, setRating] = useState(5);

  const [comment, setComment] = useState("");
  const { add } = useCart();
  const { user } = useAuth();
  const { has: wishHas, toggle: wishToggle } = useWishlist();
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState({ can_review: false, already_reviewed: false, purchased: false });

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data));
    api.get(`/reviews/${id}`).then((r) => setReviews(r.data));
    if (user) {
      api.get(`/reviews/eligibility/${id}`).then((r) => setEligibility(r.data)).catch(() => {});
    }
  }, [id, user]);

  // Seller can close their shop via dashboard toggle. When the shop is closed,
  // all products from that seller become non-purchasable — block add-to-cart,
  // buy-now, and show a banner explaining why. Chat masih boleh agar buyer
  // bisa menanyakan kapan toko buka lagi.
  const isShopOpen = product ? product.seller_is_open !== false : true;

  // Produk instan dari undeadshopp.com adalah topup langsung ke akun game,
  // jadi buyer WAJIB mengisi User ID (dan Zone/Server untuk Mobile Legends)
  // sebelum bisa masuk keranjang. Data ini ikut sampai ke API undeadshopp.
  const isInstantTopup = product?.source === "undeadshopp";
  const needsZoneId =
    isInstantTopup &&
    /mobile\s*legend|\bml\b|mlbb/i.test(`${product?.title || ""} ${product?.category || ""}`);

  const validateGameFields = () => {
    if (!isInstantTopup) return true;
    if (!gameFields.user_id.trim()) {
      toast.error("Isi User ID game dulu ya.");
      return false;
    }
    if (needsZoneId && !gameFields.zone_id.trim()) {
      toast.error("Isi Zone/Server ID (Mobile Legends) dulu ya.");
      return false;
    }
    return true;
  };

  const buildGameFields = () =>
    isInstantTopup
      ? {
          user_id: gameFields.user_id.trim(),
          ...(gameFields.zone_id.trim() ? { zone_id: gameFields.zone_id.trim() } : {}),
        }
      : null;

  const handleAdd = () => {
    if (!product) return;
    if (!isShopOpen) {
      toast.error("Toko sedang tutup. Produk tidak bisa dibeli sampai toko dibuka kembali.");
      return;
    }
    if (!validateGameFields()) return;
    add(product, qty, buildGameFields());
    toast.success("Added to cart");
  };

  const handleBuyNow = () => {
    if (!product) return;
    if (!isShopOpen) {
      toast.error("Toko sedang tutup. Produk tidak bisa dibeli sampai toko dibuka kembali.");
      return;
    }
    if (!validateGameFields()) return;
    add(product, qty, buildGameFields());
    navigate("/cart");
  };


  const handleChat = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    navigate(`/chat?to=${product.seller_id}&product=${product.id}`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      await api.post("/reviews", { product_id: id, rating, comment });
      const r = await api.get(`/reviews/${id}`);
      setReviews(r.data);
      setComment("");
      setEligibility({ can_review: false, already_reviewed: true, purchased: true });
      toast.success("Review berhasil dikirim");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal posting review");
    }
  };

  if (!product) return <div className="text-white/50 text-center py-32">Loading...</div>;

  const showInstantDelivery = hasInstantDelivery(product);
  // Flash sale aktif untuk produk ini (null kalau tidak ada). Harga yang
  // ditagih backend saat checkout mengikuti flash_price ini.
  const flash = product.flash_sale || null;


  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="relative border border-white/10 bg-[#0a0a0a] aspect-square overflow-hidden">
          {product.image_url && (
            <img src={resolveImg(product.image_url)} alt={product.title} className="w-full h-full object-cover" />
          )}
          {showInstantDelivery && (
            <div className="absolute bottom-3 left-3 z-10">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gradient-to-r from-[#00F0FF] to-[#7C4DFF] text-black text-xs font-bold uppercase tracking-wide shadow-[0_0_16px_rgba(0,240,255,0.75)] animate-pulse">
                <Rocket className="w-4 h-4" />
                Pengiriman Instan
              </span>
            </div>
          )}
        </div>

        <div>
          <span className="tag tag-accent">{product.type}</span>
          <h1 data-testid="product-title" className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mt-3 leading-tight">
            {product.title}
          </h1>
          <div className="flex items-center gap-4 mt-3 text-sm text-white/60">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
              {product.rating?.toFixed(1) || "0.0"} ({product.review_count || 0} reviews)
            </div>
            <span>•</span>
            <span>{product.sold || 0} sold</span>
            {typeof product.stock === "number" && (
              <>
                <span>•</span>
                <span className={product.stock > 0 ? "text-[#00FF66] font-semibold" : "text-[#FF6B6B] font-semibold"}>
                  {product.stock > 0 ? `Stok ${product.stock}` : "Stok habis"}
                </span>
              </>
            )}
          </div>

          <p className="text-white/70 mt-6 leading-relaxed whitespace-pre-wrap break-words">{product.description}</p>

          {flash ? (
            /* Saat produk sedang flash sale, harga coret + harga flash + sisa
               stok flash ditonjolkan. Harga inilah yang ditagih backend. */
            <div
              data-testid="product-flash-price"
              className="mt-8 border border-[#FF003C]/50 bg-[#FF003C]/10 p-4"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#FF6B6B] font-bold">
                <Flame className="w-4 h-4" /> Flash Sale −{flash.discount_percent}%
              </div>
              <div className="mt-2 flex items-end gap-3 flex-wrap">
                <span className="font-display font-black text-4xl text-[#FF003C] leading-none">
                  {formatIDR(flash.flash_price)}
                </span>
                <span className="text-lg text-white/40 line-through leading-none">
                  {formatIDR(product.price)}
                </span>
                <span className="text-xs uppercase tracking-[0.2em] text-[#00FF66] leading-none">
                  Hemat {formatIDR(flash.saving)}
                </span>
              </div>
              <div className="mt-2 text-xs text-white/60">
                Sisa <span className="text-white font-bold">{flash.remaining}</span> dari {flash.stock} slot ·
                berakhir {new Date(flash.ends_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}
              </div>
              {/* Batas pembelian per ID game — aturan yang sama ditegakkan backend
                  saat checkout, jadi pembeli tahu lebih awal sebelum bayar. */}
              {flash.limit_per_game_id && (
                <div
                  data-testid="product-flash-limit"
                  className="mt-2 flex items-start gap-2 text-xs text-[#FFB020]"
                >
                  <Info className="w-4 h-4 shrink-0 mt-[1px]" />
                  <span>
                    Maksimal <span className="font-bold">{flash.max_per_game_id}x</span> per ID game.
                    Pembelian berikutnya dengan ID yang sama dikenakan harga normal.
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-8 flex items-center gap-3">
              <span className="font-display font-black text-4xl text-[#00F0FF]">{formatIDR(product.price)}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-white/40">IDR</span>
            </div>
          )}


          {/* Form tujuan topup — hanya untuk produk instan undeadshopp.
              Nilai di sini dikirim ke API sebagai data_no / data_zone. */}
          {isInstantTopup && (
            <div data-testid="game-target-form" className="mt-6 border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-[#00F0FF] font-bold mb-1">
                Tujuan Topup
              </p>
              <p className="text-[11px] text-white/50 mb-3">
                Diamond dikirim langsung ke akun ini. Pastikan ID benar — pesanan
                tidak bisa dibatalkan setelah diproses.
              </p>
              <div className={`grid gap-3 ${needsZoneId ? "grid-cols-2" : "grid-cols-1"}`}>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/60">User ID</label>
                  <input
                    data-testid="game-user-id"
                    value={gameFields.user_id}
                    onChange={(e) => setGameFields((f) => ({ ...f, user_id: e.target.value }))}
                    placeholder="contoh: 123456789"
                    className="w-full bg-black border border-white/15 focus:border-[#00F0FF] focus:outline-none text-white text-sm px-3 py-2.5 mt-1"
                  />
                </div>
                {needsZoneId && (
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/60">Zone / Server ID</label>
                    <input
                      data-testid="game-zone-id"
                      value={gameFields.zone_id}
                      onChange={(e) => setGameFields((f) => ({ ...f, zone_id: e.target.value }))}
                      placeholder="contoh: 2001"
                      className="w-full bg-black border border-white/15 focus:border-[#00F0FF] focus:outline-none text-white text-sm px-3 py-2.5 mt-1"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.2em] text-white/60">QTY</span>

            <div className="flex border border-white/15">
              <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 hover:bg-white/5">−</button>
              <input data-testid="qty-input" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-12 text-center bg-transparent border-x border-white/15" />
              <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="w-10 h-10 hover:bg-white/5">+</button>
            </div>
          </div>

          {/* Banner "Toko Tutup" tampil saat seller menutup tokonya. Tombol
              Add to Cart & Buy Now dinonaktifkan (visual + handler). Chat
              tetap aktif agar buyer bisa menanyakan kapan toko buka. */}
          {!isShopOpen && (
            <div
              data-testid="shop-closed-banner"
              className="mt-6 border border-[#FF003C]/50 bg-[#FF003C]/10 text-[#FFB3B3] px-4 py-3 text-sm flex items-center gap-2"
            >
              <span className="text-lg">🛑</span>
              <div>
                <div className="font-bold text-white">Toko sedang tutup</div>
                <div className="text-xs">Produk ini tidak bisa dibeli sampai toko dibuka kembali. Kamu masih bisa chat seller untuk menanyakan jadwal buka.</div>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              data-testid="add-to-cart-btn"
              onClick={handleAdd}
              disabled={!isShopOpen}
              title={!isShopOpen ? "Toko sedang tutup" : "Tambah ke keranjang"}
              className={`btn-ghost inline-flex items-center gap-2 ${!isShopOpen ? "opacity-40 cursor-not-allowed hover:!bg-transparent" : ""}`}
            >
              <ShoppingCart className="w-4 h-4" /> Add to Cart
            </button>
            <button
              data-testid="buy-now-btn"
              onClick={handleBuyNow}
              disabled={!isShopOpen}
              title={!isShopOpen ? "Toko sedang tutup" : "Beli sekarang"}
              className={`btn-primary inline-flex items-center gap-2 ${!isShopOpen ? "opacity-40 cursor-not-allowed hover:!bg-[#00F0FF] hover:!text-black" : ""}`}
            >
              Buy Now
            </button>
            <button data-testid="chat-seller-btn" onClick={handleChat} className="btn-ghost inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> Chat Seller
            </button>
            <button
              data-testid="wishlist-detail-btn"
              onClick={async () => {
                if (!user) return navigate("/login");
                const r = await wishToggle(product.id);
                toast.success(r.in_wishlist ? "Added to wishlist" : "Removed from wishlist");
              }}
              className={`btn-ghost inline-flex items-center gap-2 ${wishHas(product.id) ? "!border-[#FF003C] !text-[#FF003C]" : ""}`}
            >
              <Heart className={`w-4 h-4 ${wishHas(product.id) ? "fill-current" : ""}`} />
              {wishHas(product.id) ? "Wishlisted" : "Wishlist"}
            </button>
          </div>

          <div className="mt-8 border-t border-white/10 pt-6">
            <h4 className="text-xs uppercase font-bold tracking-[0.2em] text-white/60 mb-3">Seller</h4>
            <SellerCard
              sellerId={product.seller_id}
              fallbackName={product.seller_name}
              onChat={handleChat}
            />
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { icon: Zap, t: "Instant" },
              { icon: Shield, t: "Escrow" },
              { icon: Star, t: "Top Rated" },
            ].map((b) => (
              <div key={b.t} className="border border-white/10 p-3 text-center">
                <b.icon className="w-4 h-4 mx-auto text-[#00F0FF]" />
                <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">{b.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-16">
        <h2 className="font-display font-black text-2xl md:text-3xl text-white mb-6">Review ({reviews.length})</h2>
        {user && eligibility.can_review && (
          <form onSubmit={submitReview} className="border border-[#00FF66]/30 bg-[#00FF66]/5 p-6 mb-8" data-testid="review-form">
            <p className="text-xs uppercase tracking-widest text-[#00FF66] mb-3">Tulis review Anda</p>
            <div className="flex items-center gap-2 mb-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`star-${n}`}
                  onClick={() => setRating(n)}
                  className={n <= rating ? "text-[#FFD700]" : "text-white/20"}
                >
                  <Star className="w-5 h-5 fill-current" />
                </button>
              ))}
              <span className="text-white/60 text-sm ml-2">{rating}/5</span>
            </div>
            <textarea
              data-testid="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              required
              minLength={3}
              maxLength={1000}
              placeholder="Bagaimana pengalaman Anda dengan produk ini?"
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] focus:outline-none text-white p-3 text-sm"
              rows={3}
            />
            <button data-testid="review-submit-btn" type="submit" className="btn-primary mt-3">
              Kirim Review
            </button>
          </form>
        )}
        {user && eligibility.already_reviewed && (
          <div className="border border-white/10 bg-white/5 p-4 mb-8 text-sm text-white/70" data-testid="review-already">
            ✓ Anda sudah memberi review untuk produk ini.
          </div>
        )}
        {user && !eligibility.purchased && !eligibility.already_reviewed && (
          <div className="border border-white/10 bg-white/5 p-4 mb-8 text-sm text-white/60" data-testid="review-locked">
            <p className="font-bold text-white/80 mb-1">📦 Hanya pembeli terverifikasi yang bisa review</p>
            <p className="text-xs">Beli produk ini & selesaikan pesanannya untuk bisa memberi review.</p>
          </div>
        )}
        <div className="space-y-4">
          {reviews.length === 0 && <p className="text-white/50">Belum ada review. Jadilah yang pertama!</p>}
          {reviews.map((r) => (
            <div key={r.id} className="border border-white/10 p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-white text-sm">{r.user_name || "Anonim"}</span>
                <div className="flex">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} className="w-3 h-3 fill-[#FFD700] text-[#FFD700]" />
                  ))}
                </div>
              </div>
              <p className="text-white/70 text-sm whitespace-pre-wrap">{r.comment}</p>
              {r.created_at && <p className="text-[10px] uppercase tracking-widest text-white/30 mt-2">{new Date(r.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</p>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
